import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericGet } from '@/_shared/types';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_REPORTS_TRAINING_PATH_KEY } from '@/swagger/constants/reports-keys';
import { API_GET_REPORTS_TRAININGS, KEY_GET_REPORTS_TRAININGS } from '../constants';
import { MOCK_RESPONSE_GET_REPORTS_TRAININGS } from '../mocks';

type GetReportsTrainings = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_REPORTS_TRAINING_PATH_KEY
>;

type RequestGetReportsTrainingsQuery = GetReportsTrainings['request']['query'];

type ResponseGetReportsTrainings = GetReportsTrainings['response']['body'];

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsGetReportsTrainings, 'successSaga' | 'errorSaga'>;
};

export type CallParamsGetReportsTrainings = RequestGetReportsTrainingsQuery & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

export type CallResponseGetReportsTrainings = ResponseGetReportsTrainings;

type CallErrorGetReportsTrainings = {
  message: string;
  code: number;
};

type CallSelectorsGetReportsTrainings = {
  data: ResponseGetReportsTrainings['data'];
  totalItems: ResponseGetReportsTrainings['totalItems'];
  fullRequest: FullRequestType;
};

let internalPayload: Omit<CallParamsGetReportsTrainings, 'successSaga' | 'errorSaga'> = {} as Omit<
  CallParamsGetReportsTrainings,
  'successSaga' | 'errorSaga'
>;

export const getReportsTrainingsCall = createGeneralCall<
  CallParamsGetReportsTrainings,
  CallResponseGetReportsTrainings,
  CallErrorGetReportsTrainings,
  CallSelectorsGetReportsTrainings
>({
  id: KEY_GET_REPORTS_TRAININGS,
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_REPORTS_TRAININGS(org as unknown as string, payload);
    },
  paramsSelector:
    ({ payload }) =>
    () => {
      internalPayload = { ...payload };
      return payload;
    },
  method: 'get',
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_REPORTS_TRAININGS : undefined,
  selectors: {
    data: ({ selectors }) => createSelector(selectors.response, res => res?.data || []),
    totalItems: ({ selectors }) => createSelector(selectors.response, res => res?.totalItems || 0),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
  },
  cancellable: true,
});
